import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Trophy, UserPlus, ArrowUpDown, Info, Gamepad2, BellOff } from 'lucide-react'
import { motion } from 'framer-motion'
import { pageContainer, fadeInUp, staggerList, listItem, tapScale } from '@/lib/motion'
import { cn, formatRelativeTime } from '@/lib/utils'
import { useNotificationStore, type NotificationType } from '@/stores/notificationStore'
import { EmptyState } from '@/components/shared/EmptyState'

const typeConfig: Record<NotificationType, { icon: typeof Trophy; color: string; bg: string; label: string }> = {
  jackpot_win: { icon: Trophy, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10', label: '獎池' },
  room_invite: { icon: UserPlus, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10', label: '邀請' },
  transaction: { icon: ArrowUpDown, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10', label: '交易' },
  system: { icon: Info, color: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-500/10', label: '系統' },
  game: { icon: Gamepad2, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-500/10', label: '遊戲' },
}

const filterTabs = [
  { label: '全部', value: 'all' },
  { label: '獎池', value: 'jackpot_win' },
  { label: '邀請', value: 'room_invite' },
  { label: '交易', value: 'transaction' },
  { label: '系統', value: 'system' },
  { label: '遊戲', value: 'game' },
] as const

export function NotificationHistoryScreen() {
  const navigate = useNavigate()
  const history = useNotificationStore((s) => s.history)
  const markAsRead = useNotificationStore((s) => s.markAsRead)
  const [activeFilter, setActiveFilter] = useState<string>('all')

  const filtered = activeFilter === 'all'
    ? history
    : history.filter((n) => n.type === activeFilter)

  return (
    <motion.div
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full bg-background relative"
    >
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] gradient-radial-gold pointer-events-none" />
      <div className="fixed inset-0 noise-overlay pointer-events-none" />

      {/* Header */}
      <motion.header
        variants={fadeInUp}
        className="flex-shrink-0 flex items-center gap-4 px-4 py-4 safe-area-top sticky top-0 bg-background/80 backdrop-blur-xl border-b border-foreground/5 z-40"
      >
        <motion.button
          whileTap={tapScale}
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full glass-card hover:border-gold/30 transition-all"
        >
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </motion.button>
        <h1 className="font-display text-xl font-semibold text-foreground flex-1">通知紀錄</h1>
      </motion.header>

      {/* Filter tabs */}
      <motion.div variants={fadeInUp} className="flex-shrink-0 px-4 py-3 relative z-10">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
          {filterTabs.map((tab) => (
            <motion.button
              key={tab.value}
              whileTap={tapScale}
              onClick={() => setActiveFilter(tab.value)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all',
                activeFilter === tab.value
                  ? 'bg-gold/20 text-gold border border-gold/30'
                  : 'glass-card text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Notification list */}
      <motion.main
        variants={pageContainer}
        className="flex-1 overflow-y-auto px-4 pb-24 relative z-10 scrollbar-hide"
      >
        {filtered.length === 0 ? (
          <EmptyState
            icon={BellOff}
            title="沒有通知"
            description="此分類中沒有任何通知紀錄。"
          />
        ) : (
          <motion.div
            variants={staggerList}
            initial="hidden"
            animate="visible"
            className="space-y-2"
          >
            {filtered.map((notification) => {
              const config = typeConfig[notification.type]
              const Icon = config.icon

              return (
                <motion.div
                  key={notification.id}
                  variants={listItem}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                  className={cn(
                    'relative flex items-start gap-3 p-4 glass-card rounded-2xl transition-colors',
                    !notification.read && 'border-amber-400/20 bg-amber-400/[0.02]'
                  )}
                >
                  {/* Unread dot */}
                  {!notification.read && (
                    <span className="absolute top-4 left-1.5 w-1.5 h-1.5 rounded-full bg-amber-400" />
                  )}

                  {/* Type icon */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center ml-1`}>
                    <Icon className={`w-5 h-5 ${config.color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-0.5">
                      <p className={cn(
                        'text-sm font-semibold leading-tight',
                        notification.read ? 'text-foreground/80' : 'text-foreground'
                      )}>
                        {notification.title}
                      </p>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0 mt-0.5">
                        {formatRelativeTime(notification.timestamp)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {notification.message}
                    </p>
                    <span className={cn(
                      'inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold',
                      config.bg, config.color
                    )}>
                      {config.label}
                    </span>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>
        )}
      </motion.main>
    </motion.div>
  )
}
